import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, Container, Row, Col,
  Input, Button, InputGroup, InputGroupAddon
} from "reactstrap";
import api from "../../services/api";
import PlayYouTubeVideo from "../../components/Modals/PlayYouTubeVideo.js";
import { getYouTubeVideoUrl } from "../../utils/youtubeCitations.js";

const DEFAULT_MESSAGES = [
  { role: 'assistant', content: 'Olá! Sou o <b>SmartChat</b>. Como posso ajudar com os processos SAP hoje?' }
];

// Página de chat pública, pensada para ser carregada dentro de um <iframe>
// em outros sistemas (ex: LiveDesk). Sem menu/cabeçalho do SmartFlow, sem
// exigir login — chama o mesmo endpoint público usado pelo SmartChat interno.
const SmartChatEmbedPage = () => {
  const [messages, setMessages] = useState(() => {
    const savedChat = localStorage.getItem('smartflow_embed_chat_history');
    if (!savedChat) return DEFAULT_MESSAGES;

    try {
      const parsedChat = JSON.parse(savedChat);
      return Array.isArray(parsedChat) ? parsedChat : DEFAULT_MESSAGES;
    } catch (error) {
      localStorage.removeItem('smartflow_embed_chat_history');
      return DEFAULT_MESSAGES;
    }
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoModalOpened, setVideoModalOpened] = useState(false);
  const [videoModalSource, setVideoModalSource] = useState('');
  const [videoModalTitle, setVideoModalTitle] = useState('');
  const messagesEndRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const messagesRef = useRef(messages);
  const abortControllerRef = useRef(null);
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    messagesRef.current = messages;
    localStorage.setItem('smartflow_embed_chat_history', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      requestAnimationFrame(() => {
        if (lastUserMessageRef.current) {
          lastUserMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const handleMessageClick = (e) => {
    const link = e.target.closest('a');
    const videoUrl = link && getYouTubeVideoUrl(link.getAttribute('href'));
    if (!videoUrl) return;

    e.preventDefault();

    const citation = link.closest('.citation-mark');
    setVideoModalTitle(citation?.getAttribute('data-tooltip') || 'Vídeo');
    setVideoModalSource(videoUrl);
    setVideoModalOpened(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpened(false);
    setVideoModalSource('');
    setVideoModalTitle('');
  };

  const clearChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    messagesRef.current = DEFAULT_MESSAGES;
    setMessages(DEFAULT_MESSAGES);
    setInput('');
    setLoading(false);
    localStorage.setItem('smartflow_embed_chat_history', JSON.stringify(DEFAULT_MESSAGES));
  };

  const handleSend = async (overrideQuestion) => {
    const questionToSend = overrideQuestion ?? input;
    if (!questionToSend.trim() || loading) return;

    const userMsg = { role: 'user', content: questionToSend };
    const currentMessages = messagesRef.current;
    const updatedMessages = [...currentMessages, userMsg];
    const currentQuestion = questionToSend;
    const historyToSend = currentMessages
      .filter(message =>
        message &&
        message.content &&
        !(message.role === 'assistant' && message.content.includes('Sou o <b>SmartChat</b>'))
      )
      .slice(-10);
    const abortController = new AbortController();

    abortControllerRef.current = abortController;

    setMessages([...updatedMessages, { role: 'assistant', content: '', source: null }]);
    setInput('');
    setLoading(true);

    try {
      const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${baseURL}/google-chat/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: currentQuestion,
          history: historyToSend,
          source: 'livedesk-embed',
        }),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error('Falha na conexão com o servidor.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedText = '';
      let source = null;
      let suggestions = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const raw = part.slice(6).trim();
          if (raw === '[DONE]') continue;

          try {
            const parsed = JSON.parse(raw);
            if (parsed.error) {
              accumulatedText = parsed.error;
            } else if (parsed.source !== undefined) {
              source = parsed.source;
            } else if (parsed.text) {
              accumulatedText += parsed.text;
            }

            if (Array.isArray(parsed.suggestions) && parsed.suggestions.length > 0) {
              suggestions = parsed.suggestions;
            }

            const nextContent = accumulatedText;
            const nextSource = source;
            const nextSuggestions = suggestions;

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: nextContent,
                source: nextSource,
                suggestions: nextSuggestions,
              };
              return updated;
            });
          } catch (e) {
            continue;
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Erro ao conectar ao servidor do SmartFlow.',
        };
        return updated;
      });
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      setLoading(false);
    }
  };

  return (
    <div style={{ background: "#f6f9fc", minHeight: "100vh" }}>
      <Container fluid style={{ padding: "12px" }}>
        <Row>
          <Col md="12">
            <Card className="shadow border-0" style={{ height: "calc(100vh - 24px)", display: "flex", flexDirection: "column" }}>
              <CardHeader className="d-flex align-items-center justify-content-between bg-white border-bottom" style={{ padding: "0.75rem 1.5rem" }}>
                <h4 className="mb-0">SmartChat</h4>
                <Button className="btn-neutral" color="default" onClick={clearChat} size="sm">
                  Limpar Conversa
                </Button>
              </CardHeader>
              <CardBody
                style={{ flex: 1, overflowY: "auto", padding: "1.5rem" }}
                onClick={handleMessageClick}
              >
                {(() => {
                  let lastUserIndex = -1;
                  for (let idx = messages.length - 1; idx >= 0; idx--) {
                    if (messages[idx].role === 'user') {
                      lastUserIndex = idx;
                      break;
                    }
                  }
                  return messages.map((msg, i) => (
                  <div key={i} className="mb-4" style={{ scrollMarginTop: "16px" }} ref={i === lastUserIndex ? lastUserMessageRef : null}>
                    <div className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                      <div className={`p-3 shadow-sm ${msg.role === 'user' ? 'bg-info text-white' : 'bg-white text-dark'}`}
                           style={{ borderRadius: "15px", maxWidth: "80%" }}>
                        <div
                          className="smart-chat-message-content"
                          dangerouslySetInnerHTML={{ __html: msg.content }}
                        />
                      </div>
                    </div>

                    {msg.role === 'assistant' && !loading && i === messages.length - 1 &&
                      Array.isArray(msg.suggestions) && msg.suggestions.length > 0 && (
                      <div className="d-flex flex-wrap mt-2" style={{ gap: "8px" }}>
                        {msg.suggestions.map((suggestion, si) => (
                          <button
                            key={si}
                            type="button"
                            onClick={() => handleSend(suggestion)}
                            disabled={loading}
                            className="btn btn-sm"
                            style={{
                              borderRadius: "16px",
                              border: "1px solid #11cdef",
                              background: "#fff",
                              color: "#11cdef",
                              fontSize: "0.8rem",
                              padding: "6px 14px",
                            }}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  ));
                })()}
                {loading && <div className="text-muted small mb-3">
                  <i className="fa fa-circle-notch fa-spin mr-2"></i>Analisando base de conhecimento...
                </div>}
                <div ref={messagesEndRef} />
              </CardBody>

              <div className="p-3 border-top bg-white">
                <InputGroup className="input-group-merge shadow-none border-1">
                  <Input
                    placeholder="Sua dúvida técnica..."
                    type="text"
                    value={input}
                    style={{ border: "1px solid #dee2e6", borderRadius: "25px 0 0 25px", paddingLeft: "20px" }}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      color="info"
                      onClick={() => handleSend()}
                      disabled={loading}
                      style={{ borderRadius: "0 25px 25px 0", padding: "0 25px" }}
                    >
                      {loading ? '...' : 'Enviar'}
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
      <PlayYouTubeVideo
        title={videoModalTitle}
        modalOpened={videoModalOpened}
        modalClose={closeVideoModal}
        source={videoModalSource}
      />
    </div>
  );
};

export default SmartChatEmbedPage;
